export type MarketplaceCapture = (
  event: string,
  properties?: Record<string, unknown>,
) => void;
export type MarketplaceRole = "agent" | "lo";
export type MarketplaceMarketState = "active" | "thin" | "empty";
export type MarketplaceCohort = "organic" | "referred";
export type ConsentForkChoice = "switch_now" | "window";
export type ShareKind = "buyer_brag" | "offer_share" | "standing_teaser";
export type ClaimTier = "t1" | "t2" | "t3";
/**
 * The two (and only two) consumer surfaces that link to the public
 * "How the marketplace works" article (M11-B3, plan 326): the moments a buyer
 * is deciding whether to expose themselves. Adding a third surface here is a
 * spec change, not a wiring change.
 */
export type HowItWorksSurface = "publish_dialog" | "browse_waitlist";
export type BuyerAgentGuideSurface = "home_hero";
/** The two descriptor filters on the browse panel/sheet (plan 444 M5). */
export type BrowseDescriptorFilter = "language" | "specialty";
/** What the signals line shows on a card; derived in `browse-card-impressions.ts`. */
export type BrowseCardSignalState = "joined_only" | "partial" | "full";

export const MARKETPLACE_ANALYTICS_EVENTS = {
  browseViewed: "browse_viewed",
  browseCardClicked: "browse_card_clicked",
  consentForkShown: "consent_fork_shown",
  consentForkChoice: "consent_fork_choice",
  interstitialShown: "interstitial_shown",
  interstitialDismissed: "interstitial_dismissed",
  shareCardRendered: "share_card_rendered",
  claimResumed: "claim_resumed",
  claimCompleted: "claim_completed",
  howItWorksClicked: "marketplace_how_it_works_clicked",
  buyerAgentGuideClicked: "buyer_agent_guide_clicked",
  // Plan 444 M5 (design spec §9). The other two plan-444 events live elsewhere:
  // profile_descriptors_saved fires server-side, profile_photo_changed from the
  // Pro app.
  browseFilterApplied: "marketplace.browse_filter_applied",
  browseCardRendered: "marketplace.browse_card_rendered",
  // Plan 462 M5 (no-contact exit). The matching `completed` event fires
  // server-side from the disconnect handler.
  noContactExitShown: "marketplace.no_contact_exit_shown",
  noContactExitClicked: "marketplace.no_contact_exit_clicked",
} as const;

export function captureBrowseViewed(
  capture: MarketplaceCapture,
  role: MarketplaceRole,
  marketState: MarketplaceMarketState,
  cohort: MarketplaceCohort,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.browseViewed, {
    role,
    market_state: marketState,
    cohort,
  });
}

export function captureBrowseCardClicked(
  capture: MarketplaceCapture,
  rankPosition: number,
  hasStandingOffer: boolean,
  isYourAgent: boolean,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.browseCardClicked, {
    rank_position: rankPosition,
    has_standing_offer: hasStandingOffer,
    is_your_agent: isYourAgent,
  });
}

export function captureInterstitialShown(capture: MarketplaceCapture) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.interstitialShown);
}

export function captureInterstitialDismissed(capture: MarketplaceCapture) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.interstitialDismissed);
}

// Plan 255 owns the consent-sheet mount. These typed helpers are ready for
// that wrapper without fabricating an analytics emission in preview or an
// unrelated surface.
export function captureConsentForkShown(capture: MarketplaceCapture) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.consentForkShown);
}

export function captureConsentForkChoice(
  capture: MarketplaceCapture,
  choice: ConsentForkChoice,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.consentForkChoice, { choice });
}

// M11 share/claim telemetry is intentionally event-specific. These helpers
// construct the complete property bags rather than accepting caller props, so
// public and claim-only data cannot leak through an otherwise generic capture.
export function captureShareCardRendered(capture: MarketplaceCapture) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.shareCardRendered, { kind: "buyer_brag" });
}

export function captureClaimResumed(capture: MarketplaceCapture, tier: ClaimTier) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.claimResumed, { tier });
}

export function captureClaimCompleted(capture: MarketplaceCapture, inArea: boolean) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.claimCompleted, { in_area: inArea });
}

export function captureHowItWorksClicked(
  capture: MarketplaceCapture,
  surface: HowItWorksSurface,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.howItWorksClicked, { surface });
}

export function captureBuyerAgentGuideClicked(
  capture: MarketplaceCapture,
  surface: BuyerAgentGuideSurface,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.buyerAgentGuideClicked, { surface });
}

const BROWSE_DESCRIPTOR_FILTERS: readonly BrowseDescriptorFilter[] = ["language", "specialty"];
const BROWSE_CARD_SIGNAL_STATES: readonly BrowseCardSignalState[] = ["joined_only", "partial", "full"];

/** Fires once per language/specialty option the buyer selects. The bag is the
 * filter kind alone: never the selected code, the result count, or the area. */
export function captureBrowseFilterApplied(
  capture: MarketplaceCapture,
  filter: BrowseDescriptorFilter,
) {
  if (!BROWSE_DESCRIPTOR_FILTERS.includes(filter)) return;
  capture(MARKETPLACE_ANALYTICS_EVENTS.browseFilterApplied, { filter });
}

export interface BrowseCardRenderedProps {
  has_photo: boolean;
  has_specialties: boolean;
  has_closes: boolean;
  has_responsiveness: boolean;
  signal_state: BrowseCardSignalState;
}

/** One sampled card impression. The bag is rebuilt field by field so nothing
 * about the agent (id, name, photo URL, descriptor values, counts) can ride
 * along, and an out-of-enum signal_state is refused rather than sent. */
export function captureBrowseCardRendered(
  capture: MarketplaceCapture,
  props: BrowseCardRenderedProps,
) {
  if (!BROWSE_CARD_SIGNAL_STATES.includes(props.signal_state)) return;
  capture(MARKETPLACE_ANALYTICS_EVENTS.browseCardRendered, {
    has_photo: props.has_photo === true,
    has_specialties: props.has_specialties === true,
    has_closes: props.has_closes === true,
    has_responsiveness: props.has_responsiveness === true,
    signal_state: props.signal_state,
  });
}

// Plan 462 M5. The role is the ONLY property: no connection id, agent name or
// date ever rides these events (ADR-030 D13). The role is coerced to the enum
// so a stray value cannot become an arbitrary string on the wire.
function exitRole(role: string): MarketplaceRole {
  return role === "lo" ? "lo" : "agent";
}

export function captureNoContactExitShown(
  capture: MarketplaceCapture,
  role: string,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.noContactExitShown, {
    role: exitRole(role),
  });
}

export function captureNoContactExitClicked(
  capture: MarketplaceCapture,
  role: string,
) {
  capture(MARKETPLACE_ANALYTICS_EVENTS.noContactExitClicked, {
    role: exitRole(role),
  });
}

const NO_CONTACT_EXIT_SHOWN_STORAGE_KEY = "sill.marketplace.no_contact_exit_shown";
const noContactExitShownThisPage = new Set<string>();

/**
 * Once-per-connection-per-session guard for `no_contact_exit_shown`. Returns
 * true exactly the first time a connection id is claimed in this browser
 * session, so remounting the banner (Home <-> Offers, a refetch) never emits
 * a second impression. The id is the guard's key only; it never reaches the
 * event. The in-memory set holds even when sessionStorage throws (private
 * mode, SSR), in which case the guard is per page load.
 */
export function claimNoContactExitShown(connectionId: string): boolean {
  if (noContactExitShownThisPage.has(connectionId)) return false;
  noContactExitShownThisPage.add(connectionId);
  try {
    const raw = window.sessionStorage.getItem(NO_CONTACT_EXIT_SHOWN_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const seen = Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
    if (seen.includes(connectionId)) return false;
    window.sessionStorage.setItem(
      NO_CONTACT_EXIT_SHOWN_STORAGE_KEY,
      JSON.stringify([...seen, connectionId]),
    );
  } catch {
    // Storage unavailable or corrupt: the in-memory guard above still holds.
  }
  return true;
}

/** Test seam: forget every claimed impression, in memory and in storage. */
export function resetNoContactExitShownForTests() {
  noContactExitShownThisPage.clear();
  try {
    window.sessionStorage.removeItem(NO_CONTACT_EXIT_SHOWN_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
